<template>
  <div v-if="variant === 'button'" class="file-preview-inline">
    <button
      v-if="currentFile && hasPreviewSource(currentFile)"
      type="button"
      class="file-preview-button"
      :title="isPreviewable(currentFile) ? '预览' : '下载查看'"
      @click.stop="onItemClick(currentFile, currentIndex)"
    >
      <svg v-if="isPreviewable(currentFile)" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
      </svg>
      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/>
      </svg>
      <span>{{ isPreviewable(currentFile) ? '预览' : '下载查看' }}</span>
    </button>
    <FilePreviewModal
      v-model="previewVisible"
      :file="currentFile"
      :loader="loader"
      :show-prev="fileList.length > 1 && currentIndex > 0"
      :show-next="fileList.length > 1 && currentIndex < fileList.length - 1"
      @prev="goPrev"
      @next="goNext"
    />
  </div>

  <div v-else class="space-y-2">
    <div
      v-for="(file, idx) in fileList"
      :key="file.fileKey || file.url || idx"
      class="file-att-item"
      :class="{ 'is-clickable': isClickable(file) }"
      @click="onItemClick(file, idx)"
    >
      <FileIcon :file="file" :thumb="true" class="w-9 h-9 shrink-0" />

      <div class="file-att-main">
        <span class="file-att-name" :title="getFileName(file)">{{ getFileName(file) }}</span>
        <div class="file-att-meta">
          <span v-if="getFileSize(file)">{{ getFileSize(file) }}</span>
          <span v-if="getFileSize(file) && isPreviewable(file)" class="meta-sep">·</span>
          <span v-if="isPreviewable(file)" class="meta-preview">可预览</span>
          <span v-else class="meta-download">下载查看</span>
        </div>
      </div>

      <button
        v-if="hasPreviewSource(file)"
        type="button"
        class="file-att-action"
        :title="isPreviewable(file) ? '预览' : '下载'"
        @click.stop="onItemClick(file, idx)"
      >
        <svg v-if="isPreviewable(file)" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
        </svg>
        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/>
        </svg>
      </button>

      <button
        v-if="removable"
        type="button"
        class="file-att-remove"
        :disabled="isRemoving(file)"
        :title="isRemoving(file) ? '删除中' : '删除'"
        @click.stop="$emit('remove', file)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0a1 1 0 011-1h6a1 1 0 011 1m-8 0h8"/>
        </svg>
      </button>
    </div>

    <FilePreviewModal
      v-model="previewVisible"
      :file="currentFile"
      :loader="loader"
      :show-prev="fileList.length > 1 && currentIndex > 0"
      :show-next="fileList.length > 1 && currentIndex < fileList.length - 1"
      @prev="goPrev"
      @next="goNext"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import FileIcon from './FileIcon.vue'
import FilePreviewModal from './FilePreviewModal.vue'
import { getFileName, getFileSize, getFileUrl, isPreviewable } from '@/utils/fileType'

const props = defineProps({
  // 数组或单值
  files: { type: [Array, Object, String], default: () => [] },
  // 是否允许点击预览（false 时仅展示，无点击行为）
  previewable: { type: Boolean, default: true },
  variant: { type: String, default: 'list' },
  loader: { type: Function, default: null },
  removable: { type: Boolean, default: false },
  removingId: { type: [Number, String], default: null }
})
defineEmits(['remove'])

const previewVisible = ref(false)
const currentIndex = ref(0)

const fileList = computed(() => {
  if (!props.files) return []
  if (Array.isArray(props.files)) return props.files
  return [props.files]
})
const currentFile = computed(() => fileList.value[currentIndex.value] || null)

function isClickable(file) {
  return props.previewable && hasPreviewSource(file)
}

function hasPreviewSource(file) {
  return Boolean(getFileUrl(file) || (props.loader && typeof file === 'object' && file.previewMode === 'blob'))
}

function onItemClick(file, idx) {
  if (!props.previewable) return
  if (!hasPreviewSource(file)) return
  if (isPreviewable(file)) {
    currentIndex.value = idx
    previewVisible.value = true
  } else {
    downloadFile(file)
  }
}

function isRemoving(file) {
  if (props.removingId == null) return false
  const id = getFileIdentity(file)
  return id != null && String(id) === String(props.removingId)
}

function getFileIdentity(file) {
  if (!file || typeof file === 'string') return null
  return file.attachmentId ?? file.id ?? file.fileKey ?? file.url ?? null
}

function goPrev() {
  if (currentIndex.value > 0) currentIndex.value--
}

function goNext() {
  if (currentIndex.value < fileList.value.length - 1) currentIndex.value++
}

async function downloadFile(file) {
  if (props.loader && typeof file === 'object' && file.previewMode === 'blob') {
    const result = await props.loader(file, { purpose: 'download' })
    const payload = result?.data ?? result
    if (payload instanceof Blob) {
      const blobUrl = URL.createObjectURL(payload)
      triggerDownload(blobUrl, getFileName(file))
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
      return
    }
    if (typeof payload === 'string') {
      triggerDownload(payload, getFileName(file))
      return
    }
  }
  triggerDownload(getDownloadSource(file) || getFileUrl(file), getFileName(file))
}

function getDownloadSource(file) {
  if (!file || typeof file === 'string') return ''
  return file.downloadSource || file.downloadUrl || ''
}

function triggerDownload(url, name) {
  if (!url) return
  const a = document.createElement('a')
  a.href = url
  a.download = name || ''
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
</script>

<style scoped>
.file-att-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  transition: border-color .15s, background .15s;
}
.file-att-item.is-clickable {
  cursor: pointer;
}
.file-att-item.is-clickable:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}
.file-att-main {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.file-att-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
  font-weight: 500;
  font-size: 13px;
}
.file-att-meta {
  font-size: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
}
.meta-sep {
  color: #cbd5e1;
}
.meta-preview {
  color: #2563eb;
}
.meta-download {
  color: #94a3b8;
}
.file-att-action {
  flex: 0 0 auto;
  padding: 6px;
  color: #2563eb;
  border-radius: 6px;
  transition: background .15s;
}
.file-att-remove {
  flex: 0 0 auto;
  padding: 6px;
  color: #94a3b8;
  border-radius: 6px;
  transition: background .15s, color .15s;
}
.file-att-remove:hover:not(:disabled) {
  color: #dc2626;
  background: #fef2f2;
}
.file-att-remove:disabled {
  cursor: not-allowed;
  opacity: .55;
}
.file-att-action:hover {
  background: #dbeafe;
  color: #1d4ed8;
}
.file-preview-inline {
  display: inline-flex;
  align-items: center;
}
.file-preview-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 30px;
  padding: 5px 9px;
  border-radius: 6px;
  color: #2563eb;
  background: #eff6ff;
  transition: background .15s, color .15s;
  font-size: 12px;
  line-height: 1;
}
.file-preview-button:hover {
  color: #1d4ed8;
  background: #dbeafe;
}
</style>
